import { ReactNode, useEffect, useMemo } from 'react';

import { GetUnitsResponseDTO, IssueDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import excavatorVector from '@/assets/vectors/excavator.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useToast } from '@/components/ui/toaster';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createAssetIssueTableColumns } from './table-meta';

export const AssetIssuesTable = ({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  onRowClick,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  units,
}: {
  notFoundDisplay: ReactNode;
  data: IssueDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  onRowClick: (row: IssueDTO) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  units?: GetUnitsResponseDTO['data'];
}) => {
  const navigate = useWorkspaceNavigate();
  const toast = useToast();
  const { syncModuleColumns } = useModulesStore();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  async function handleWorkOrderClick(workOrderId: string) {
    try {
      const workOrderData = (await (await ApiService.GetWorkOrderById({ workOrderId })).json()).workOrder;
      navigate('/work-orders', { state: { workOrder: workOrderData } });
    } catch (error) {
      console.error('Error fetching work order:', error);
      toast.error('Failed to load work order');
    }
  }

  function handleRepairClick(repairId: string) {
    navigate('/issues/repairs/activity', { state: { repairId } });
  }

  useEffect(() => {
    syncModuleColumns(MODULE.AssetIssues, createAssetIssueTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<IssueDTO>
        data={data}
        columns={createAssetIssueTableColumns({ onWorkOrderClick: handleWorkOrderClick, onRepairClick: handleRepairClick, units })}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={onRowClick}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
};

export const NoIssueAvailableDisplay = () => {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={excavatorVector} alt="Excavator" className="h-[114.5px] w-[220px]" />
      <span className="text-xl leading-[30px] font-medium">No issues yet</span>
    </div>
  );
};

export const NoIssueFoundFromSearchDisplay = () => {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No issue match your search</span>
    </div>
  );
};

export const NoIssueFoundFromFiltersDisplay = ({ removeColumnFilters }: { removeColumnFilters: () => void }) => {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No issue match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
};
